/* eslint-disable @typescript-eslint/ban-ts-comment */
import { PivotControls } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Matrix4, Quaternion, Vector3 } from 'three';

import PhysicalBoundaryModel from '../../models/standard/PhysicalBoundary';
import RoomModel from '../../models/standard/RoomModel';
import { ISpace } from '../../models/standard/SpaceModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { getSpaceFloorShape } from '../../utils/spaceUtils';
import { isPointInPolygon } from '../../utils/vectorUtils';
import Space from './Space';

const Z_AXIS = new THREE.Vector3(0, 0, 1);
const GIZMO_Z_OFFSET = 10;
const EVENT_PRIORITY_Z = 0;
const ROOM_ROTATION_SNAP_DEGREES = 1;
const ROOM_ROTATION_SNAP_RADIANS = THREE.MathUtils.degToRad(ROOM_ROTATION_SNAP_DEGREES);
const ROOM_GRID_AXIS_SNAP_DEGREES = 90;
const ROOM_GRID_AXIS_SNAP_RADIANS = THREE.MathUtils.degToRad(ROOM_GRID_AXIS_SNAP_DEGREES);
const ROOM_GRID_AXIS_SNAP_THRESHOLD_DEGREES = 3;
const ROOM_GRID_AXIS_SNAP_THRESHOLD_RADIANS = THREE.MathUtils.degToRad(ROOM_GRID_AXIS_SNAP_THRESHOLD_DEGREES);
const ROOM_SHIFT_ROTATION_SNAP_DEGREES = 15;
const ROOM_SHIFT_ROTATION_SNAP_RADIANS = THREE.MathUtils.degToRad(ROOM_SHIFT_ROTATION_SNAP_DEGREES);

const EPS_TRANSLATE = 0.001;
const EPS_ROTATE = 0.0001;
const POLY_TOL = 1e-4; // or your EPS
type PivotDragComponent = 'Arrow' | 'Slider' | 'Rotator' | 'Sphere';

// ----------------- helpers -----------------

const normalizeRotation = (angle: number): number => {
  const TWO_PI = Math.PI * 2;
  // treat huge values as degrees
  if (Math.abs(angle) > TWO_PI * 2) return ((angle % 360) + 360) % 360;
  return ((angle % TWO_PI) + TWO_PI) % TWO_PI;
};

const getShortestAngleDistance = (a: number, b: number): number => {
  const TWO_PI = Math.PI * 2;
  return Math.atan2(Math.sin(a - b), Math.cos(a - b)) % TWO_PI;
};

const snapRotationZAngle = (angle: number, snapRadians: number, gridAlignedWallAngles: number[] = [], gridAxisSnapThresholdRadians = 0): number => {
  if (snapRadians <= 0) return angle;

  if (gridAxisSnapThresholdRadians > 0 && gridAlignedWallAngles.length > 0) {
    let closestWallAlignedRotation: number | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const wallAngle of gridAlignedWallAngles) {
      const targetWallAngle = Math.round((wallAngle + angle) / ROOM_GRID_AXIS_SNAP_RADIANS) * ROOM_GRID_AXIS_SNAP_RADIANS;
      const wallAlignedRotation = targetWallAngle - wallAngle;
      const distance = Math.abs(getShortestAngleDistance(angle, wallAlignedRotation));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestWallAlignedRotation = wallAlignedRotation;
      }
    }

    if (closestWallAlignedRotation !== null && closestDistance <= gridAxisSnapThresholdRadians) {
      return closestWallAlignedRotation;
    }
  }

  return Math.round(angle / snapRadians) * snapRadians;
};

const snapPivotRotationZ = (matrix: Matrix4, snapRadians: number, gridAlignedWallAngles: number[] = [], gridAxisSnapThresholdRadians = 0): Matrix4 => {
  if (snapRadians <= 0) return matrix.clone();

  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  matrix.decompose(position, quaternion, scale);

  const euler = new THREE.Euler().setFromQuaternion(quaternion);
  euler.z = snapRotationZAngle(euler.z, snapRadians, gridAlignedWallAngles, gridAxisSnapThresholdRadians);
  quaternion.setFromEuler(euler);

  return new Matrix4().compose(position, quaternion, scale);
};

const getSpaceGridAlignedWallAngles = (space: ISpace): number[] => {
  const angles: number[] = [];
  const seen = new Set<string>();

  const addWallAngle = (wall: any) => {
    const start = wall?.startPoint;
    const end = wall?.endPoint;
    if (!start || !end) return;

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return;

    const angle = Math.atan2(dy, dx);
    const normalizedAngle = ((angle % ROOM_GRID_AXIS_SNAP_RADIANS) + ROOM_GRID_AXIS_SNAP_RADIANS) % ROOM_GRID_AXIS_SNAP_RADIANS;
    const key = normalizedAngle.toFixed(6);
    if (seen.has(key)) return;

    seen.add(key);
    angles.push(normalizedAngle);
  };

  const primary = getPrimaryBoundary(space);
  const edges = (primary?.edges ?? []) as any[];
  for (const edge of edges) addWallAngle(edge?.wall);

  if (angles.length === 0) {
    for (const wall of (space.walls ?? []) as any[]) addWallAngle(wall);
  }

  return angles;
};

const getPrimaryBoundary = (space: any): PhysicalBoundaryModel | undefined => {
  const boundaries = Array.isArray(space?.boundaries) ? space.boundaries : [];
  return boundaries.find((b: any) => b instanceof PhysicalBoundaryModel) as PhysicalBoundaryModel | undefined;
};

const getPerimeterWallUuidsForRoomFromBuilding = (building: any, roomUuid: string): Set<string> => {
  const out = new Set<string>();
  const room = (building.rooms ?? []).find((r: any) => r?.uuid === roomUuid);
  if (!room) return out;

  for (const space of room.spaces ?? []) {
    const primary = getPrimaryBoundary(space);
    const edges = (primary?.edges ?? []) as any[];
    for (const e of edges) {
      const id = e?.wall?.uuid;
      if (id) out.add(id);
    }
  }
  return out;
};
// replace shapeContainsPoint2D with this (no ShapeUtils)
const shapeContainsPoint2D = (shape: THREE.Shape, p: THREE.Vector2): boolean => {
  // extractPoints gives you polygon loops for outer + holes
  const extracted = shape.extractPoints(12);
  const outer = extracted.shape; // THREE.Vector2[]
  const holes = extracted.holes ?? []; // THREE.Vector2[][]

  if (!outer || outer.length < 3) return false;

  // inside outer?
  const inOuter = isPointInPolygon(p, outer, POLY_TOL);
  if (!inOuter) return false;

  // not inside any hole
  for (const hole of holes) {
    if (!hole || hole.length < 3) continue;
    if (isPointInPolygon(p, hole, 0)) return false;
  }

  return true;
};

const SAMPLE_EPS = 1e-3;
export function getSpaceUuidAtPointByGeometry(building: any, worldPos: THREE.Vector3): string | null {
  const rooms = building?.rooms ?? [];
  if (!rooms.length) return null;

  const base = new THREE.Vector2(worldPos.x, worldPos.y);
  const offsets: Array<[number, number]> = [
    [0, 0],
    [SAMPLE_EPS, 0],
    [-SAMPLE_EPS, 0],
    [0, SAMPLE_EPS],
    [0, -SAMPLE_EPS],
  ];

  let best: { spaceUuid: string; hits: number } | null = null;

  for (const room of rooms) {
    for (const space of room?.spaces ?? []) {
      const spaceUuid = space?.uuid;
      if (!spaceUuid) continue;

      let shape: THREE.Shape | null = null;
      try {
        shape = getSpaceFloorShape(space);
      } catch {
        shape = null;
      }
      if (!shape) continue;

      let hits = 0;
      for (const [dx, dy] of offsets) {
        const pt = new THREE.Vector2(base.x + dx, base.y + dy);
        if (shapeContainsPoint2D(shape, pt)) hits++;
      }

      if (hits > 0 && (!best || hits > best.hits)) best = { spaceUuid, hits };
      if (best?.hits === offsets.length) return best.spaceUuid;
    }
  }

  return best?.spaceUuid ?? null;
}

// Map current building space uuid -> room uuid (ephemeral; only valid in this snapshot)
const buildSpaceToRoomIndex = (building: any): Map<string, string> => {
  const m = new Map<string, string>();
  for (const room of building?.rooms ?? []) {
    if (!room?.uuid) continue;
    for (const space of room?.spaces ?? []) {
      if (space?.uuid) m.set(space.uuid, room.uuid);
    }
  }
  return m;
};

/**
 * Build index: wallUuid -> set(roomUuid) for PERIMETER usage only (from boundary edges).
 * This survives splits and ignores “polluted” space.walls lists.
 */
const buildPerimeterIndex = (building: any): Map<string, Set<string>> => {
  const m = new Map<string, Set<string>>();
  for (const room of building.rooms ?? []) {
    const rUuid = room?.uuid;
    if (!rUuid) continue;

    for (const space of room.spaces ?? []) {
      const primary = getPrimaryBoundary(space);
      const edges = (primary?.edges ?? []) as any[];

      for (const e of edges) {
        const wUuid = e?.wall?.uuid;
        if (!wUuid) continue;
        if (!m.has(wUuid)) m.set(wUuid, new Set<string>());
        m.get(wUuid)!.add(rUuid);
      }
    }
  }
  return m;
};

const isWallSharedByIndex = (perimeterIndex: Map<string, Set<string>>, wallUuid: string, thisRoomUuid: string) => {
  const set = perimeterIndex.get(wallUuid);
  if (!set) return false;
  if (!set.has(thisRoomUuid)) return false;
  return set.size > 1;
};

const getRobustSpaceCenter = (space: ISpace): Vector3 => {
  try {
    const shape = getSpaceFloorShape(space);
    const points = shape.getPoints();
    if (points && points.length > 0) {
      const box = new THREE.Box3().setFromPoints(points.map((p) => new THREE.Vector3(p.x, p.y, 0)));
      const center = new THREE.Vector3();
      box.getCenter(center);
      center.z = 0;
      return center;
    }
  } catch {
    // fall back below
  }

  const walls = (space.walls || []) as any[];
  if (walls.length === 0) return new Vector3(0, 0, 0);

  let sumX = 0;
  let sumY = 0;
  let count = 0;
  walls.forEach((wall: any) => {
    if (wall.startPoint && wall.endPoint) {
      sumX += wall.startPoint.x + wall.endPoint.x;
      sumY += wall.startPoint.y + wall.endPoint.y;
      count += 2;
    }
  });

  return count === 0 ? new Vector3(0, 0, 0) : new Vector3(sumX / count, sumY / count, 0);
};

// ----------------- main transform logic -----------------

const applyRoomTransform = (
  mode: 'translate' | 'rotate',
  activeRoomUuid: string,
  building: any,
  perimeterIndex: Map<string, Set<string>>,
  val: Vector3 | number,
  center: Vector3,
): { newBuildingWalls: any[]; newRooms: any[] } => {
  // ✅ Source of truth: perimeter walls from boundary edges (NOT space.walls)
  const perimeterWallUuids = getPerimeterWallUuidsForRoomFromBuilding(building, activeRoomUuid);

  // Optional: include “floating” walls (partitions etc.) BUT only if they are NOT used as perimeter by any room.
  const floatingWallUuids = new Set<string>();
  const activeRoom = (building.rooms ?? []).find((r: any) => r?.uuid === activeRoomUuid);

  if (activeRoom) {
    for (const space of activeRoom.spaces ?? []) {
      for (const w of (space?.walls ?? []) as any[]) {
        const id = w?.uuid;
        if (!id) continue;
        if (perimeterWallUuids.has(id)) continue;
        if (perimeterIndex.has(id)) continue;
        floatingWallUuids.add(id);
      }
    }
  }

  const activeWallUuids = new Set<string>([...perimeterWallUuids, ...floatingWallUuids]);
  const changedWallsMap = new Map<string, any>();

  let transformFn: (pt: Vector3) => void;

  if (mode === 'translate') {
    const offset = val as Vector3;
    const flatOffset = new THREE.Vector3(offset.x, offset.y, 0);
    transformFn = (pt) => pt.add(flatOffset);
  } else {
    const angle = val as number;
    const safeCenter = new THREE.Vector3(center.x, center.y, 0);
    transformFn = (pt) => pt.sub(safeCenter).applyAxisAngle(Z_AXIS, angle).add(safeCenter);
  }

  const cloneAndTransformVec = (v: any): Vector3 => {
    const vec = v instanceof THREE.Vector3 ? v.clone() : new THREE.Vector3(v.x, v.y, v.z ?? 0);
    transformFn(vec);
    return vec;
  };

  const transformAttachment = (obj: any): any => {
    if (!obj || !obj.position) return obj;
    const clone = Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);
    clone.position = cloneAndTransformVec(obj.position);

    if (mode === 'rotate' && typeof obj.angle === 'number') {
      const delta = val as number;
      clone.angle = normalizeRotation(obj.angle + delta);
    }
    return clone;
  };

  (building.walls || []).forEach((wall: any) => {
    const id = wall?.uuid;
    if (!id) return;
    if (!activeWallUuids.has(id)) return;

    const newWall = Object.assign(Object.create(Object.getPrototypeOf(wall)), wall);

    if (newWall.startPoint) newWall.startPoint = cloneAndTransformVec(newWall.startPoint);
    if (newWall.endPoint) newWall.endPoint = cloneAndTransformVec(newWall.endPoint);

    if (Array.isArray(newWall.windows)) newWall.windows = newWall.windows.map(transformAttachment);
    if (Array.isArray(newWall.doors)) newWall.doors = newWall.doors.map(transformAttachment);
    if (Array.isArray(newWall.passages)) newWall.passages = newWall.passages.map(transformAttachment);
    if (newWall.sides) {
      const sides = newWall.sides as any;
      (['A', 'B'] as const).forEach((sideKey) => {
        const side = sides[sideKey];
        if (!side || !Array.isArray(side.radiators)) return;

        const newSide = Object.assign(Object.create(Object.getPrototypeOf(side)), side);
        newSide.radiators = side.radiators.map(transformAttachment);
        sides[sideKey] = newSide;
      });
      newWall.sides = sides;
    }

    if (typeof newWall.resetPoints === 'function') newWall.resetPoints();

    changedWallsMap.set(id, newWall);
  });

  const newBuildingWalls = (building.walls || []).map((w: any) => (w?.uuid && changedWallsMap.has(w.uuid) ? changedWallsMap.get(w.uuid) : w));

  const newRooms = (building.rooms || []).map((room: any) => {
    const isActive = room?.uuid === activeRoomUuid;
    if (!isActive) return room;

    const newSpaces = (room.spaces || []).map((space: any) => {
      const nextSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);

      nextSpace.walls = (space.walls || []).map((w: any) => (w?.uuid && changedWallsMap.has(w.uuid) ? changedWallsMap.get(w.uuid) : w));

      nextSpace.boundaries = (space.boundaries || []).map((b: any) => {
        const newBoundary = Object.assign(Object.getPrototypeOf(b) ? Object.create(Object.getPrototypeOf(b)) : {}, b);

        if (Array.isArray(newBoundary.walls)) {
          newBoundary.walls = newBoundary.walls.map((w: any) => (w?.uuid && changedWallsMap.has(w.uuid) ? changedWallsMap.get(w.uuid) : w));
        }

        if (Array.isArray(newBoundary.edges)) {
          newBoundary.edges = newBoundary.edges.map((edge: any) => {
            const wUuid = edge?.wall?.uuid;
            if (wUuid && changedWallsMap.has(wUuid)) {
              const newEdge = Object.assign(Object.getPrototypeOf(edge) ? Object.create(Object.getPrototypeOf(edge)) : {}, edge);
              newEdge.wall = changedWallsMap.get(wUuid);
              if (typeof newEdge.resetPoints === 'function') newEdge.resetPoints();
              return newEdge;
            }
            return edge;
          });
        }

        return newBoundary;
      });

      const transformItem = (item: any): any => {
        if (!item || !item.position) return item;

        const newItem = Object.assign(Object.create(Object.getPrototypeOf(item)), item);
        newItem.position = cloneAndTransformVec(item.position);

        if (mode === 'rotate' && newItem.rotation !== null) {
          const delta = val as number;
          if (typeof newItem.rotation === 'number') {
            newItem.rotation = normalizeRotation(newItem.rotation - delta);
          } else if (newItem.rotation instanceof THREE.Euler) {
            const e = newItem.rotation.clone();
            e.z = normalizeRotation(e.z - delta);
            newItem.rotation = e;
          }
        }
        return newItem;
      };

      if (Array.isArray(space.elements)) nextSpace.elements = space.elements.map(transformItem);
      if (Array.isArray(space.products)) nextSpace.products = space.products.map(transformItem);
      if (Array.isArray(space.models2D)) nextSpace.models2D = space.models2D.map(transformItem);

      return nextSpace;
    });

    const nextRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
    nextRoom.spaces = newSpaces;
    return nextRoom;
  });

  return { newBuildingWalls, newRooms };
};

// -------- sprites: move with room by geometry --------

const cloneWithProto = <T,>(obj: T): T => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);

const isSpriteInRoomByGeometry = (building: any, spritePos: THREE.Vector3, roomUuid: string): boolean => {
  const spaceUuid = getSpaceUuidAtPointByGeometry(building, spritePos);
  if (!spaceUuid) return false;
  const idx = buildSpaceToRoomIndex(building);
  return idx.get(spaceUuid) === roomUuid;
};

const applyTranslateToPos = (pos: THREE.Vector3, offset: THREE.Vector3) => {
  pos.add(new THREE.Vector3(offset.x, offset.y, 0));
};

const applyRotateToPos = (pos: THREE.Vector3, angle: number, center: THREE.Vector3) => {
  const c = new THREE.Vector3(center.x, center.y, 0);
  pos.sub(c).applyAxisAngle(Z_AXIS, angle).add(c);
};

// ----------------- TransformableSpace -----------------

interface TransformableSpaceProps {
  space: ISpace;
  roomModel: RoomModel;
  building: any;
  setBuilding: (b: any) => void;
  perimeterIndex: Map<string, Set<string>>;
  handleSpaceClick: (id: string) => void;
  onFloorPointerMove: (e: ThreeEvent<PointerEvent>) => void;
  onFloorPointerDown: (e: ThreeEvent<PointerEvent>) => void;
  isSelected: boolean;
}

const TransformableSpace: React.FC<TransformableSpaceProps> = ({
  space,
  roomModel,
  building,
  setBuilding,
  perimeterIndex,
  handleSpaceClick,
  onFloorPointerMove,
  onFloorPointerDown,
  isSelected,
}) => {
  const pivotControlsRef = useRef<THREE.Group>(null);
  const matrixRef = useRef(new Matrix4());
  const activeDragComponentRef = useRef<PivotDragComponent | null>(null);
  const isShiftPressedRef = useRef(false);
  const [version, setVersion] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const { cameraPositionSprites, updateCameraPositionSprites } = useAnnotations();
  const { isSnappingEnabled } = useMultiSelectToggleContext();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') isShiftPressedRef.current = true;
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') isShiftPressedRef.current = false;
    };

    const handleWindowBlur = () => {
      isShiftPressedRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const boundaryWallUuids = useMemo(() => {
    const primary = getPrimaryBoundary(space);
    const edges = (primary?.edges ?? []).filter(Boolean) as any[];
    return edges.map((e) => e?.wall?.uuid).filter(Boolean) as string[];
  }, [space]);

  const isLocked = useMemo(() => {
    const unique = Array.from(new Set(boundaryWallUuids));
    return unique.some((uuid) => isWallSharedByIndex(perimeterIndex, uuid, roomModel.uuid));
  }, [boundaryWallUuids, perimeterIndex, roomModel.uuid]);

  const spaceCenter = useMemo(() => getRobustSpaceCenter(space), [space, version]);
  const gridAlignedWallAngles = useMemo(() => getSpaceGridAlignedWallAngles(space), [space, version]);

  const handleDragStart = ({ component }: { component: PivotDragComponent }) => {
    activeDragComponentRef.current = component;
    setIsDragging(true);
  };

  const handleDrag = (matrix: Matrix4) => {
    const isRotating = activeDragComponentRef.current === 'Rotator';
    const shouldSnapToShiftRotation = isRotating && isShiftPressedRef.current;
    const shouldSnapToSettingRotation = isRotating && isSnappingEnabled;
    const shouldSnapRotation = shouldSnapToShiftRotation || shouldSnapToSettingRotation;

    const nextMatrix = shouldSnapToShiftRotation
      ? snapPivotRotationZ(matrix, ROOM_SHIFT_ROTATION_SNAP_RADIANS)
      : shouldSnapToSettingRotation
        ? snapPivotRotationZ(matrix, ROOM_ROTATION_SNAP_RADIANS, gridAlignedWallAngles, ROOM_GRID_AXIS_SNAP_THRESHOLD_RADIANS)
        : matrix;

    matrixRef.current.copy(nextMatrix);

    if (shouldSnapRotation && pivotControlsRef.current) {
      pivotControlsRef.current.matrix.copy(nextMatrix);
      pivotControlsRef.current.updateMatrixWorld(true);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    const dragComponent = activeDragComponentRef.current;

    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    matrixRef.current.decompose(position, quaternion, scale);

    const euler = new THREE.Euler().setFromQuaternion(quaternion);
    const isRotating = dragComponent === 'Rotator';
    const rotationZ =
      isRotating && isShiftPressedRef.current
        ? snapRotationZAngle(euler.z, ROOM_SHIFT_ROTATION_SNAP_RADIANS)
        : isRotating && isSnappingEnabled
          ? snapRotationZAngle(euler.z, ROOM_ROTATION_SNAP_RADIANS, gridAlignedWallAngles, ROOM_GRID_AXIS_SNAP_THRESHOLD_RADIANS)
          : euler.z;

    position.z = 0;

    const hasMoved = position.lengthSq() > EPS_TRANSLATE;
    const hasRotated = Math.abs(rotationZ) > EPS_ROTATE;

    if (!hasMoved && !hasRotated) {
      activeDragComponentRef.current = null;
      matrixRef.current.identity();
      return;
    }

    const buildingBefore = building;
    const roomUuid = roomModel.uuid;

    // Decide which sprites belong to this room BEFORE transform (by geometry)
    const spriteKeysToMove = new Set<string>();
    for (const spr of cameraPositionSprites ?? []) {
      const key = (spr as any)?.uuid ?? `id:${(spr as any)?.id ?? ''}`;
      if (!key) continue;
      const pos = (spr as any).position as THREE.Vector3;
      if (!pos) continue;
      if (isSpriteInRoomByGeometry(buildingBefore, pos, roomUuid)) spriteKeysToMove.add(key);
    }

    let currentBuilding = buildingBefore;

    if (hasMoved) {
      const { newBuildingWalls, newRooms } = applyRoomTransform('translate', roomUuid, currentBuilding, perimeterIndex, position, spaceCenter);
      currentBuilding = { ...currentBuilding, walls: newBuildingWalls, rooms: newRooms };
    }

    if (hasRotated) {
      const { newBuildingWalls, newRooms } = applyRoomTransform('rotate', roomUuid, currentBuilding, perimeterIndex, rotationZ, spaceCenter);
      currentBuilding = { ...currentBuilding, walls: newBuildingWalls, rooms: newRooms };
    }

    if (spriteKeysToMove.size > 0) {
      updateCameraPositionSprites((prev) => {
        const moved = (prev ?? []).map((spr: any) => {
          const key = spr?.uuid ?? `id:${spr?.id ?? ''}`;
          if (!spriteKeysToMove.has(key)) return spr;

          const nextSpr = cloneWithProto(spr);
          nextSpr.position = spr.position instanceof THREE.Vector3 ? spr.position.clone() : new THREE.Vector3(spr.position.x, spr.position.y, spr.position.z ?? 0);

          if (hasMoved) applyTranslateToPos(nextSpr.position, position);
          if (hasRotated) applyRotateToPos(nextSpr.position, rotationZ, spaceCenter);

          // after movement, refresh ephemeral spaceUuid in the NEW building snapshot
          nextSpr.spaceUuid = getSpaceUuidAtPointByGeometry(currentBuilding, nextSpr.position) ?? null;

          return nextSpr;
        });

        return moved;
      });
    }

    setBuilding(currentBuilding);
    activeDragComponentRef.current = null;
    matrixRef.current.identity();
    setVersion((v) => v + 1);
  };

  const wrappedOnFloorPointerDown = (e: ThreeEvent<PointerEvent>) => {
    if (isDragging) return;
    onFloorPointerDown?.(e);
  };

  const handlePivotPointerDownCapture = (event: ThreeEvent<PointerEvent>) => {
    if (event.button !== 0) {
      event.stopPropagation();
    }
  };

  const totalGizmoZ = GIZMO_Z_OFFSET + EVENT_PRIORITY_Z;

  if (isSelected && !isLocked) {
    return (
      <group position={[spaceCenter.x, spaceCenter.y, totalGizmoZ]} onPointerDownCapture={handlePivotPointerDownCapture}>
        <PivotControls
          ref={pivotControlsRef}
          key={`${space.uuid}-pivot-${version}`}
          visible={true}
          scale={140}
          lineWidth={5}
          fixed={true}
          depthTest={false}
          renderOrder={9999}
          disableScaling={true}
          activeAxes={[true, true, false]}
          disableRotations={false}
          onDrag={handleDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          anchor={[0, 0, 0]}
        >
          <group position={[-spaceCenter.x, -spaceCenter.y, -totalGizmoZ]}>
            <Space
              key={`space-content-${version}`}
              roomModel={roomModel}
              spaceModel={space}
              onRoomClick={() => handleSpaceClick(space.uuid)}
              onFloorPointerMove={onFloorPointerMove}
              onFloorPointerDown={wrappedOnFloorPointerDown}
            />
          </group>
        </PivotControls>
      </group>
    );
  }

  return (
    <group position={[spaceCenter.x, spaceCenter.y, 0]}>
      <group position={[-spaceCenter.x, -spaceCenter.y, 0]}>
        <Space
          key={`space-content-${version}`}
          roomModel={roomModel}
          spaceModel={space}
          onRoomClick={() => handleSpaceClick(space.uuid)}
          onFloorPointerMove={onFloorPointerMove}
          onFloorPointerDown={wrappedOnFloorPointerDown}
        />
      </group>
    </group>
  );
};

// ----------------- Room wrapper -----------------

const Room: React.FC<{
  roomModel: RoomModel;
  onFloorPointerMove: (e: ThreeEvent<PointerEvent>) => void;
  onFloorPointerDown: (e: ThreeEvent<PointerEvent>) => void;
}> = ({ roomModel, onFloorPointerMove, onFloorPointerDown }) => {
  const { building, setBuilding } = useBuilding();
  const { selectedSpace, handleSpaceClick } = useSelection();
  const { updateCameraPositionSprites } = useAnnotations();

  const perimeterIndex = useMemo(() => buildPerimeterIndex(building), [building.rooms]);

  const safeSpaces: ISpace[] = useMemo(() => {
    const raw = Array.isArray(roomModel.spaces) ? roomModel.spaces : [];
    return raw.filter((s): s is ISpace => !!s && Array.isArray((s as any).walls));
  }, [roomModel.spaces]);

  return (
    <group name={`group-room-${roomModel.uuid}`}>
      {safeSpaces.map((space, i) => (
        <TransformableSpace
          key={space.uuid ?? i}
          space={space}
          roomModel={roomModel}
          building={building}
          setBuilding={setBuilding}
          perimeterIndex={perimeterIndex}
          handleSpaceClick={handleSpaceClick}
          onFloorPointerMove={onFloorPointerMove}
          onFloorPointerDown={onFloorPointerDown}
          isSelected={selectedSpace?.spaceId === space.uuid}
        />
      ))}
    </group>
  );
};

export default Room;
